import Cookies from 'universal-cookie';
// routes
import { PATH_AUTH } from '../routes/paths';

// ----------------------------------------------------------------------
// The login cookies are shared by every tab of a browser, but each tab keeps
// its own signed-in user and Authorization header in memory. Without this, a
// login or logout in one tab leaves the others running as the previous user
// (e.g. admin in one tab, staff in another, all hitting the API with whichever
// token the cookie holds at that moment). The rule enforced here: every tab
// always shows the user the shared cookie belongs to.
// ----------------------------------------------------------------------

const CHANNEL_NAME = 'optopro-auth-session';

// Identity (user id) this tab was signed in as. null = signed out.
let tabUserId: number | null = null;

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

const decodeUserId = (token?: string): number | null => {
  try {
    if (!token) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const id = JSON.parse(window.atob(base64))?.id;
    return typeof id === 'number' ? id : null;
  } catch {
    return null;
  }
};

/** User the shared cookies currently belong to; null if signed out. */
const cookieUserId = (): number | null => {
  const cookies = new Cookies();
  return (
    decodeUserId(cookies.get(process.env.REACT_APP_TOKEN!)) ??
    decodeUserId(cookies.get(process.env.REACT_APP_REFRESH_TOKEN!))
  );
};

/** Bring this tab back in line with the shared cookie if they disagree. */
export const syncTabSession = (): boolean => {
  const current = cookieUserId();
  if (current === tabUserId) return false;
  if (current === null) {
    // Signed out in another tab.
    tabUserId = null;
    window.location.href = PATH_AUTH.login;
  } else {
    // Signed in (possibly as someone else) in another tab: reload so the tab
    // re-initialises from the cookie and shows that user's data/permissions.
    window.location.reload();
  }
  return true;
};

/** Record this tab's identity after a login/refresh/logout and tell other tabs. */
export const bindTabSession = (accessToken: string | null) => {
  const next = accessToken ? decodeUserId(accessToken) : null;
  const changed = next !== tabUserId;
  tabUserId = next;
  if (changed) channel?.postMessage('changed');
};

export const isTabSessionStale = () =>
  tabUserId !== null && cookieUserId() !== tabUserId;

if (typeof window !== 'undefined') {
  channel?.addEventListener('message', () => {
    syncTabSession();
  });
  // Fallback when BroadcastChannel is unavailable or the tab was suspended.
  window.addEventListener('focus', () => {
    if (tabUserId !== null) syncTabSession();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && tabUserId !== null) syncTabSession();
  });
}
